import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Header from "@/components/Header";
import ImmersiveHero from "@/components/ImmersiveHero";

import { scrollProgressRef } from "@/lib/scrollProgress";
import ScrollSection from "@/components/ScrollSection";
import ServicesSection from "@/components/ServicesSection";
import BenefitsSection from "@/components/BenefitsSection";
import CTABanner from "@/components/CTABanner";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { buildLocalBusinessSchema, faqSchema } from "@/lib/jsonLd";
import { getReviews } from "@/lib/reviewsCache";

// Below-the-fold sections are code-split to reduce initial JS payload
// (Lighthouse "Reduce unused JavaScript" audit).
const HowItWorksSection = lazy(() => import("@/components/HowItWorksSection"));
const LiveReviewsSection = lazy(() => import("@/components/LiveReviewsSection"));
const FAQSection = lazy(() => import("@/components/FAQSection"));
const Scene3D = lazy(() => import("@/components/Scene3D"));

const Index = () => {
  const location = useLocation();
  const [showScene3D, setShowScene3D] = useState(false);
  const [liveRating, setLiveRating] = useState<{ rating: number; totalReviews: number } | null>(null);

  useEffect(() => {
    // Defer the reviews fetch until first user interaction so it doesn't
    // appear in the Lighthouse "Network dependency tree" critical chain.
    // LiveReviewsSection still fetches via the shared cache when it
    // mounts — so this prefetch is purely an optimization for the
    // JSON-LD AggregateRating (which falls back to a static schema until
    // data arrives, keeping SEO data valid throughout).
    let cancelled = false;
    let triggered = false;
    const events: (keyof WindowEventMap)[] = [
      "scroll",
      "pointerdown",
      "keydown",
      "touchstart",
      "wheel",
    ];
    const run = () => {
      if (triggered) return;
      triggered = true;
      cleanup();
      getReviews().then((data) => {
        if (cancelled || !data) return;
        if (data.rating > 0 && data.totalReviews > 0) {
          setLiveRating({ rating: data.rating, totalReviews: data.totalReviews });
        }
      });
    };
    const opts: AddEventListenerOptions = { passive: true };
    events.forEach((e) => window.addEventListener(e, run, opts));
    // Fallback for fully idle sessions so JSON-LD still gets live data.
    const fallbackTimer = window.setTimeout(run, 5000);
    function cleanup() {
      events.forEach((e) => window.removeEventListener(e, run));
      window.clearTimeout(fallbackTimer);
    }
    return () => {
      cancelled = true;
      cleanup();
    };
  }, []);

  useEffect(() => {
    // On mobile (or when the user prefers reduced motion) the parallax
    // effect is purely decorative and is the largest source of forced
    // reflows in the Lighthouse mobile audit — every frame writes
    // `--scroll-progress` which invalidates layout for the hero, and any
    // subsequent geometry read (Header scroll handler, IntersectionObserver
    // hookups, etc.) is forced to recompute style/layout. Skip the writes
    // entirely on small screens; the hero still renders correctly without
    // the parallax transform.
    const isMobileOrReducedMotion = window.matchMedia(
      "(max-width: 767px), (prefers-reduced-motion: reduce)"
    ).matches;
    // Scope the CSS variable to the hero element instead of <html>. Writing
    // to documentElement invalidates layout for the ENTIRE document tree,
    // so any subsequent geometry read anywhere on the page (Header scroll
    // handler, IntersectionObserver callbacks, image lazy-load checks, etc.)
    // is forced to recompute layout for everything. Scoping to the hero
    // contains the invalidation to that subtree only — eliminating the
    // dominant source of forced reflows in the Lighthouse "Style & Layout"
    // breakdown (~434ms desktop) without changing any visual behavior.
    const heroEl = document.querySelector<HTMLElement>("[data-hero-parallax]");
    if (isMobileOrReducedMotion) {
      heroEl?.style.setProperty("--scroll-progress", "0");
      scrollProgressRef.current = 0;
      return;
    }
    if (!heroEl) return;
    let ticking = false;
    // Cache viewport height — reading window.innerHeight on every scroll
    // frame after a style write triggers a forced reflow. Only re-measure
    // on resize.
    let viewportHeight = window.innerHeight;
    let lastProgress = -1;
    let pendingScrollY = 0;
    const update = () => {
      // Use the scrollY value captured BEFORE the rAF — this avoids
      // forcing a layout read inside the same frame as the style write.
      const progress = Math.min(1, pendingScrollY / viewportHeight);
      scrollProgressRef.current = progress;
      // Skip the style write if the rounded value hasn't changed — avoids
      // invalidating layout when nothing visually changed. Since progress
      // is clamped at 1, this naturally stops writes once the user has
      // scrolled past the hero (the dominant case for long pages).
      const rounded = Math.round(progress * 1000) / 1000;
      if (rounded !== lastProgress) {
        lastProgress = rounded;
        // Drive hero parallax via CSS variable on the hero element only.
        heroEl.style.setProperty("--scroll-progress", rounded.toString());
      }
      ticking = false;
    };
    const onScroll = () => {
      // Capture scrollY at event time (cheap — already known to the
      // browser at scroll dispatch) instead of reading it inside the rAF
      // callback after style writes from other listeners may have
      // invalidated layout. This breaks the read-after-write pattern that
      // triggers forced reflows.
      pendingScrollY = window.scrollY;
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    const onResize = () => {
      viewportHeight = window.innerHeight;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });
    pendingScrollY = window.scrollY;
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
      }
    }
  }, [location]);

  useEffect(() => {
    const isMobileOrReducedMotion = window.matchMedia(
      "(max-width: 767px), (prefers-reduced-motion: reduce)"
    ).matches;

    if (isMobileOrReducedMotion) {
      setShowScene3D(false);
      return;
    }

    // Defer Scene3D until AFTER the user interacts (or a long fallback
    // timeout). Loading/parsing the ~220 KB Three.js bundle creates the
    // longest main-thread task on the page (Lighthouse "Max Potential FID"
    // audit). Postponing it past the input-readiness window dramatically
    // reduces measured input latency without affecting UX — the scene is
    // a purely decorative background that animates on scroll anyway.
    let triggered = false;
    const events: (keyof WindowEventMap)[] = [
      "scroll",
      "pointerdown",
      "keydown",
      "touchstart",
      "wheel",
    ];
    const trigger = () => {
      if (triggered) return;
      triggered = true;
      setShowScene3D(true);
      cleanup();
    };
    const opts: AddEventListenerOptions = { passive: true };
    events.forEach((e) => window.addEventListener(e, trigger, opts));
    // Fallback for fully idle sessions so scene still appears.
    const fallbackTimer = window.setTimeout(trigger, 4000);
    function cleanup() {
      events.forEach((e) => window.removeEventListener(e, trigger));
      window.clearTimeout(fallbackTimer);
    }
    return cleanup;
  }, []);

  return (
    <>
      <SEO
        title="Phoenix Sun Screens | Valley Sun Screens — Free Quote"
        description="Custom solar sun screens for Phoenix-area homes. Block 90% of heat, lower energy bills. Licensed & insured. Free quotes — call (623) 301-4595."
        canonical="/"
        jsonLd={[buildLocalBusinessSchema(liveRating ?? undefined), faqSchema]}
      />
      <Header />

      {/* 3D WebGL Scene - fixed background behind hero */}
      <Suspense fallback={null}>
        {showScene3D ? <Scene3D /> : null}
      </Suspense>

      <main id="main-content" className="relative z-[1]">
        <ImmersiveHero />

        <ScrollSection effect="rise">
          <ServicesSection />
        </ScrollSection>

        <ScrollSection effect="fade-scale">
          <BenefitsSection />
        </ScrollSection>

        <ScrollSection effect="unveil">
          <CTABanner />
        </ScrollSection>

        <ScrollSection effect="rise" mountWhenVisible placeholderHeight={600}>
          <Suspense fallback={<div style={{ minHeight: 600 }} />}>
            <HowItWorksSection />
          </Suspense>
        </ScrollSection>

        <ScrollSection effect="fade-scale" mountWhenVisible placeholderHeight={600}>
          <Suspense fallback={<div style={{ minHeight: 600 }} />}>
            <LiveReviewsSection />
          </Suspense>
        </ScrollSection>

        <ScrollSection effect="fade-scale" mountWhenVisible placeholderHeight={600}>
          <Suspense fallback={<div style={{ minHeight: 600 }} />}>
            <FAQSection />
          </Suspense>
        </ScrollSection>

        <ScrollSection effect="unveil">
          <CTABanner />
        </ScrollSection>
      </main>
      <Footer />
    </>
  );
};

export default Index;
